import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCommits = () => {
  const [commits, setCommits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCommit, setEditingCommit] = useState(null);
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    photo_url: '',
    position: '',
    committed_college: '',
    graduation_year: new Date().getFullYear() + 1,
    announcement_date: new Date().toISOString().split('T')[0],
    highlights: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchCommits();
  }, []);

  const fetchCommits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/commits`);
      setCommits(response.data);
    } catch (error) {
      console.error('Error fetching commits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let photoUrl = formData.photo_url;
      
      if (selectedFile) {
        setUploadingImage(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);
        const uploadResponse = await axios.post(`${API}/upload`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        photoUrl = uploadResponse.data.url;
        setUploadingImage(false);
      }
      
      const commitData = {
        ...formData,
        photo_url: photoUrl,
        graduation_year: parseInt(formData.graduation_year),
        announcement_date: new Date(formData.announcement_date).toISOString()
      };
      
      if (editingCommit) {
        await axios.put(`${API}/commits/${editingCommit.id}`, commitData);
      } else {
        await axios.post(`${API}/commits`, commitData);
      }
      fetchCommits();
      resetForm();
    } catch (error) {
      console.error('Error saving commit:', error);
      alert('Error saving commit. Please try again.');
      setUploadingImage(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this commit?')) {
      try {
        await axios.delete(`${API}/commits/${id}`);
        fetchCommits();
      } catch (error) {
        console.error('Error deleting commit:', error);
        alert('Error deleting commit. Please try again.');
      }
    }
  };

  const handleEdit = (commit) => {
    setEditingCommit(commit);
    setFormData({
      name: commit.name,
      photo_url: commit.photo_url,
      position: commit.position,
      committed_college: commit.committed_college,
      graduation_year: commit.graduation_year,
      announcement_date: new Date(commit.announcement_date).toISOString().split('T')[0],
      highlights: commit.highlights
    });
    setImagePreview(commit.photo_url);
    setSelectedFile(null);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCommit(null);
    setFormData({
      name: '',
      photo_url: '',
      position: '',
      committed_college: '',
      graduation_year: new Date().getFullYear() + 1,
      announcement_date: new Date().toISOString().split('T')[0],
      highlights: ''
    });
    setSelectedFile(null);
    setImagePreview(null);
    setUploadingImage(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const years = useMemo(() => {
    const set = new Set(commits.map(c => c.graduation_year));
    return Array.from(set).sort();
  }, [commits]);

  const filteredCommits = useMemo(() => {
    const q = search.trim().toLowerCase();
    return commits.filter(c => {
      if (yearFilter !== 'all' && String(c.graduation_year) !== String(yearFilter)) return false;
      if (q) {
        const hay = `${c.name} ${c.position} ${c.committed_college} ${c.highlights || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [commits, search, yearFilter]);

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <Link to="/admin/dashboard" className="btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeft size={20} /> Back to Dashboard
              </Link>
              <h1 className="display-large">Manage Commits</h1>
            </div>
            <button onClick={() => setShowForm(true)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={20} /> Add Commit
            </button>
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {/* Search + Filter */}
          <div className="network-card" style={{ marginBottom: '20px', padding: '14px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 260px', minWidth: '200px' }}>
              <Search size={16} style={{ position: 'absolute', top: '50%', left: '12px', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, position, college..."
                style={{ width: '100%', padding: '10px 12px 10px 38px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)', border: '1px solid var(--border-medium)', borderRadius: '8px' }}
                data-testid="commits-search-input"
              />
            </div>
            <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)', border: '1px solid var(--border-medium)', borderRadius: '8px' }} data-testid="commits-year-filter">
              <option value="all">All Grad Years</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <div style={{ marginLeft: 'auto', color: 'var(--text-light)', fontSize: '0.85rem' }}>
              {filteredCommits.length} result{filteredCommits.length !== 1 ? 's' : ''}
            </div>
          </div>
          {showForm && (
            <div className="network-card" style={{ marginBottom: '32px' }}>
              <h2 className="heading-2">{editingCommit ? 'Edit Commit' : 'Add New Commit'}</h2>
              <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  <div className="form-group">
                    <label>Player Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Position *</label>
                    <input type="text" value={formData.position} onChange={(e) => setFormData({...formData, position: e.target.value})} required placeholder="e.g., RHP/SS" />
                  </div>
                  <div className="form-group">
                    <label>Committed College *</label>
                    <input type="text" value={formData.committed_college} onChange={(e) => setFormData({...formData, committed_college: e.target.value})} required placeholder="e.g., UCLA" />
                  </div>
                  <div className="form-group">
                    <label>Graduation Year *</label>
                    <input type="number" value={formData.graduation_year} onChange={(e) => setFormData({...formData, graduation_year: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Announcement Date *</label>
                    <input type="date" value={formData.announcement_date} onChange={(e) => setFormData({...formData, announcement_date: e.target.value})} required />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Highlights *</label>
                    <textarea value={formData.highlights} onChange={(e) => setFormData({...formData, highlights: e.target.value})} required rows="3" placeholder="All-State Selection, Perfect Game All-American" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Player Photo *</label>
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFileChange} style={{ marginBottom: '16px' }} />
                    {imagePreview && <div style={{ marginTop: '16px' }}><img src={imagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '12px', objectFit: 'cover', border: '2px solid var(--border-medium)' }} /></div>}
                    <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '8px' }}>Accepted formats: JPG, PNG, WebP (Max 5MB)</p>
                  </div>
                </div>
                <div style={{ marginTop: '24px', display: 'flex', gap: '16px' }}>
                  <button type="submit" className="btn-primary" disabled={uploadingImage}>{uploadingImage ? 'Uploading...' : (editingCommit ? 'Update Commit' : 'Add Commit')}</button>
                  <button type="button" onClick={resetForm} className="btn-secondary" disabled={uploadingImage}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <p>Loading commits...</p>
          ) : (
            <div className="network-card">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-medium)' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Position</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>College</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Year</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCommits.length === 0 ? (
                      <tr><td colSpan="6" style={{ padding: '24px', textAlign: 'center' }}>No commits match the filters</td></tr>
                    ) : (
                      filteredCommits.map(commit => (
                        <tr key={commit.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td style={{ padding: '12px' }}>{commit.name}</td>
                          <td style={{ padding: '12px' }}>{commit.position}</td>
                          <td style={{ padding: '12px' }}>{commit.committed_college}</td>
                          <td style={{ padding: '12px' }}>{commit.graduation_year}</td>
                          <td style={{ padding: '12px' }}>{new Date(commit.announcement_date).toLocaleDateString()}</td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button onClick={() => handleEdit(commit)} style={{ padding: '6px 12px', background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Edit size={16} /> Edit
                              </button>
                              <button onClick={() => handleDelete(commit.id)} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Trash2 size={16} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminCommits;